import { useQuery } from '@apollo/client';
import {
  AlertCircle,
  ArrowRightLeft,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';

import { LIST_ASSETS_QUERY } from '@/apollo/schemas/assets/listAssets';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AssetNav,
  CredentialGate,
  FieldRow,
  OpStatus,
  SessionBar,
  StatusBanner,
  WalletBanner,
} from '@/pages/walletOps/shared';
import {
  AssetTransferCreds,
  executeFullTransfer,
  FullTransferResult,
  getTokenBalance,
} from '@/services/tokenTransfer';

// ─── Main Page ────────────────────────────────────────────────────────────────

const AssetTransferPage = () => {
  const [credentials, setCredentials] = useState<AssetTransferCreds | null>(
    null,
  );
  const [assetNav, setAssetNav] = useState<AssetNav | null>(null);
  const [tokenBalance, setTokenBalance] = useState<string | null>(null);
  const [tokenBalanceLoading, setTokenBalanceLoading] = useState(false);

  const refreshTokenBalance = async (
    tokenAddress: string,
    walletAddress: string,
  ) => {
    setTokenBalanceLoading(true);
    try {
      const res = await getTokenBalance(tokenAddress, walletAddress);
      setTokenBalance(res.balance);
    } catch (err) {
      console.error('getTokenBalance failed:', err);
      setTokenBalance(null);
    } finally {
      setTokenBalanceLoading(false);
    }
  };

  const handleNavLoaded = (nav: AssetNav) => {
    setAssetNav(nav);
    if (credentials)
      refreshTokenBalance(nav.tokenAddress, credentials.walletAddress);
  };

  return (
    <div className="px-4 py-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Asset Transfer</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Issue ERC3643 security tokens from the treasury wallet. Select an
          asset to see its live NAV and calculate tokens owed against a wired
          USD amount. Syncs the cap table via CRE after each transfer.
        </p>
      </div>

      {!credentials ? (
        <CredentialGate onAuthenticate={setCredentials} />
      ) : (
        <div className="space-y-4">
          <SessionBar
            credentials={credentials}
            onLock={() => {
              setCredentials(null);
              setAssetNav(null);
              setTokenBalance(null);
            }}
          />

          <AssetSelector onNavLoaded={handleNavLoaded} />

          {assetNav && (
            <WalletBanner
              label={`Treasury — ${assetNav.symbol} Token Balance`}
              address={credentials.walletAddress}
              balance={tokenBalance}
              balanceUnit={assetNav.symbol}
              balanceLoading={tokenBalanceLoading}
              onRefresh={() =>
                refreshTokenBalance(
                  assetNav.tokenAddress,
                  credentials!.walletAddress,
                )
              }
            />
          )}

          <TokenTransferForm
            credentials={credentials}
            assetNav={assetNav}
            onSuccess={() => {
              if (assetNav && credentials) {
                refreshTokenBalance(
                  assetNav.tokenAddress,
                  credentials.walletAddress,
                );
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

// ─── Asset Selector ───────────────────────────────────────────────────────────

export const AssetSelector = ({
  onNavLoaded,
  description = 'Choose an asset to load its NAV and enable token issuance.',
  requireNav = true,
}: {
  onNavLoaded: (nav: AssetNav) => void;
  description?: string;
  /**
   * Issuance prices tokens off NAV, so Asset Transfer hides assets without one. Pages that
   * only need the token contract (Testing's whitelist/revert) pass false — otherwise an
   * environment whose assets carry no NAV, like dev today, shows an empty list.
   */
  requireNav?: boolean;
}) => {
  const [selectedNav, setSelectedNav] = useState<AssetNav | null>(null);

  const { data, loading, error } = useQuery(LIST_ASSETS_QUERY, {
    variables: { isActive: true },
    context: { headers: { authorization: 'public' } },
    fetchPolicy: 'cache-first',
  });

  const assets: any[] = (data?.listAssets?.assets ?? []).filter(
    (a: any) => (!requireNav || a.nav) && a.erc3643?.tokenAddress,
  );
  const navOf = (a: any) => (a.nav ? parseFloat(a.nav) : NaN);

  const handleSelect = (id: string) => {
    const asset = assets.find((a: any) => String(a.id) === id);
    if (!asset) return;
    const nav: AssetNav = {
      assetId: asset.id,
      name: asset.name,
      nav: navOf(asset),
      navLastUpdated: asset.navLastUpdated,
      tokenAddress: asset.erc3643.tokenAddress,
      symbol: asset.erc3643?.symbol ?? '',
      decimals: asset.erc3643?.decimals ?? 18,
    };
    setSelectedNav(nav);
    onNavLoaded(nav);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Select Asset</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            Loading assets…
          </div>
        ) : error ? (
          <p className="text-sm text-destructive flex items-center gap-1.5">
            <AlertCircle size={13} />
            Failed to load assets.
          </p>
        ) : (
          <Select onValueChange={handleSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Select a token…" />
            </SelectTrigger>
            <SelectContent>
              {assets.length === 0 && (
                <p className="px-2 py-1.5 text-sm text-muted-foreground">
                  {requireNav
                    ? 'No active assets with a NAV and a token contract.'
                    : 'No active assets with a token contract.'}
                </p>
              )}
              {assets.map((a: any) => (
                <SelectItem key={a.id} value={String(a.id)}>
                  {a.name}
                  {a.erc3643?.symbol ? ` (${a.erc3643.symbol})` : ''}
                  {a.nav
                    ? ` — $${navOf(a).toLocaleString()} NAV`
                    : a.chain
                      ? ` — ${a.chain}`
                      : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {selectedNav && (
          <div className="rounded-md border bg-muted/30 px-4 py-3 space-y-1.5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-muted-foreground">Asset</p>
                <p className="text-sm font-medium">{selectedNav.name}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">NAV per Token</p>
                <p className="text-lg font-bold">
                  {Number.isNaN(selectedNav.nav)
                    ? '—'
                    : `$${selectedNav.nav.toLocaleString()}`}
                </p>
              </div>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                Symbol:{' '}
                <span className="font-medium text-foreground">
                  {selectedNav.symbol}
                </span>
              </span>
              <span>
                Updated:{' '}
                {selectedNav.navLastUpdated
                  ? new Date(selectedNav.navLastUpdated).toLocaleString()
                  : '—'}
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground pt-0.5">
              <span className="truncate font-mono">
                {selectedNav.tokenAddress}
              </span>
              {process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL && (
                <a
                  href={`${process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL.replace(/\/$/, '')}/address/${selectedNav.tokenAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink
                    size={11}
                    className="shrink-0 hover:text-foreground"
                  />
                </a>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Token Transfer Form ──────────────────────────────────────────────────────

const TokenTransferForm = ({
  credentials,
  assetNav,
  onSuccess,
}: {
  credentials: AssetTransferCreds;
  assetNav: AssetNav | null;
  onSuccess: () => void;
}) => {
  const [form, setForm] = useState({ recipient: '', wiredUsd: '', note: '' });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<OpStatus>({ type: null, message: '' });

  const set = (f: keyof typeof form, v: string) =>
    setForm((p) => ({ ...p, [f]: v }));

  const tokensToIssue =
    assetNav && form.wiredUsd && !isNaN(parseFloat(form.wiredUsd))
      ? (parseFloat(form.wiredUsd) / assetNav.nav).toFixed(6)
      : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assetNav) return;
    if (!tokensToIssue || parseFloat(tokensToIssue) <= 0) {
      setStatus({ type: 'error', message: 'Token amount is zero or invalid.' });
      return;
    }
    setLoading(true);
    setStatus({ type: null, message: '' });
    try {
      const result: FullTransferResult = await executeFullTransfer(
        credentials,
        assetNav.tokenAddress,
        form.recipient.trim(),
        tokensToIssue,
        assetNav.assetId,
      );
      setStatus({
        type: 'success',
        message: `Issued ${result.tokens} ${assetNav.symbol} to ${result.to}.`,
        txHash: result.txHash,
        extra: result.identityRegistered
          ? `Identity registered & ${result.creTriggered ? 'cap table synced via CRE.' : 'no CRE sync.'}`
          : result.creTriggered
            ? 'Cap table synced via CRE.'
            : credentials.creWebhookUrl
              ? 'CRE sync failed — retry manually.'
              : 'No CRE webhook configured.',
      });
      setForm({ recipient: '', wiredUsd: '', note: '' });
      onSuccess();
    } catch (err: any) {
      setStatus({ type: 'error', message: err?.message ?? 'Transfer failed.' });
    } finally {
      setLoading(false);
    }
  };

  const disabled = !assetNav;

  return (
    <Card className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      <CardHeader>
        <CardTitle className="text-base">Issue Tokens</CardTitle>
        <CardDescription>
          {disabled
            ? 'Select an asset above to enable token issuance.'
            : `Registers identity if needed, then issues via treasury wallet. Relayer: ${credentials.relayerAddress.slice(0, 10)}…`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FieldRow id="tok-recipient" label="Recipient Wallet Address">
            <Input
              id="tok-recipient"
              placeholder="0x..."
              value={form.recipient}
              onChange={(e) => set('recipient', e.target.value)}
              required
            />
          </FieldRow>

          <FieldRow id="tok-usd" label="Wired Amount (USD)">
            <div className="relative">
              <Input
                id="tok-usd"
                type="number"
                min="0"
                step="any"
                placeholder="0.00"
                value={form.wiredUsd}
                onChange={(e) => set('wiredUsd', e.target.value)}
                className="pr-12"
                required
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                USD
              </span>
            </div>
          </FieldRow>

          <div className="rounded-md bg-muted/40 border px-4 py-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Tokens to issue
              </span>
              <span className="text-base font-bold">
                {tokensToIssue
                  ? `${parseFloat(tokensToIssue).toLocaleString()} ${assetNav?.symbol ?? ''}`
                  : '—'}
              </span>
            </div>
            {assetNav && tokensToIssue && (
              <p className="text-xs text-muted-foreground mt-1">
                ${parseFloat(form.wiredUsd).toLocaleString()} ÷ $
                {assetNav.nav.toLocaleString()} NAV
              </p>
            )}
          </div>

          <FieldRow id="tok-note" label="Note" optional>
            <Input
              id="tok-note"
              placeholder="Investment ID / reference"
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
            />
          </FieldRow>

          <StatusBanner status={status} />

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !form.recipient || !tokensToIssue || disabled}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <ArrowRightLeft className="mr-2 h-4 w-4" />
                Issue Tokens
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default AssetTransferPage;
